import React, { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { ArrowRight, ShieldCheck, HeartHandshake, Scale, Sparkles, Check, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import { trackEvent, trackServerEvent } from "../lib/analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* -------------------- Hero -------------------- */
function Hero() {
  const { lang } = useLang();
  return (
    <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden" data-testid="hero-section">
      <div className="blob" style={{ width: 480, height: 480, background: "#D8CDBA", top: -140, right: -120 }} />
      <div className="blob" style={{ width: 380, height: 380, background: "#E2762A", bottom: -160, left: -80, opacity: 0.14 }} />

      <div className="container-x relative grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7 reveal">
          <div className="overline mb-6" data-testid="hero-overline">{pick(T.hero.overline, lang)}</div>
          <h1 className="font-serif-display serif-textured text-5xl sm:text-6xl lg:text-7xl leading-[1.02] font-light text-[#1A1A1A] mb-8" data-testid="hero-h1">
            {pick(T.hero.h1, lang)}
          </h1>
          <p className="text-lg sm:text-xl text-[#4A4640] max-w-2xl leading-relaxed mb-10" data-testid="hero-sub">
            {pick(T.hero.sub, lang)}
          </p>
          <div className="flex flex-wrap gap-4 mb-10">
            <a href="#lead" className="btn-primary" data-testid="hero-cta-primary">
              {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
            </a>
            <a href={`tel:${T.phone.number}`} className="btn-secondary border-[#A83F32] text-[#A83F32] hover:bg-[#A83F32] hover:text-white" data-testid="hero-cta-call">
              <Phone size={16} strokeWidth={2} /> {pick(T.hero.ctaCall, lang)} — {T.phone.display}
            </a>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#736F66]">
            {[T.hero.trust1, T.hero.trust2, T.hero.trust3].map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`hero-trust-${i}`}>
                <Check size={16} className="text-[#2C4C3B]" strokeWidth={1.75} />
                {pick(t, lang)}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative reveal">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1564020435666-f67ed5319a32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxob2xkaW5nJTIwaGFuZHMlMjBjb21mb3J0JTIwc3VwcG9ydHxlbnwwfHx8fDE3ODMwMDE4NjB8MA&ixlib=rb-4.1.0&q=85"
              alt="Comfort and support"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C4C3B]/40 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-8 -left-6 bg-white rounded-2xl shadow-xl p-5 max-w-[240px] border border-[#D8CDBA]/60 hidden sm:block overflow-hidden">
            <div className="tricolor-band mb-4 -mx-5 -mt-5" data-testid="hero-tricolor" style={{ borderRadius: 0 }} />
            <div className="overline mb-2">{lang === "en" ? "Verified" : "सत्यापित"}</div>
            <div className="font-serif-display text-2xl leading-tight text-[#2C4C3B]">
              {lang === "en" ? "128 lawyers vetted by real High Court outcomes." : "128 वकील असली हाई कोर्ट नतीजों से जांचे गए।"}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Problem/Solution -------------------- */
function Problem() {
  const { lang } = useLang();
  return (
    <section className="section-y bg-[#EAE6DF] relative overflow-hidden" data-testid="problem-section">
      <div className="container-x grid md:grid-cols-12 gap-14 items-start">
        <div className="md:col-span-5 reveal">
          <div className="overline mb-5">{pick(T.problem.overline, lang)}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] font-medium text-[#1A1A1A]" data-testid="problem-h2">
            {pick(T.problem.h2, lang)}
          </h2>
        </div>
        <div className="md:col-span-7 reveal">
          <p className="text-lg text-[#4A4640] leading-relaxed whitespace-pre-line" data-testid="problem-body">
            {pick(T.problem.body, lang)}
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Crisis (India's undertrial reality) -------------------- */
function Crisis() {
  const { lang } = useLang();
  const stats = [
    { n: T.crisis.stat1.num, l: T.crisis.stat1.label },
    { n: T.crisis.stat2.num, l: T.crisis.stat2.label },
    { n: T.crisis.stat3.num, l: T.crisis.stat3.label },
  ];
  return (
    <section
      className="section-y bg-[#1F3A2C] text-white relative overflow-hidden"
      data-testid="crisis-section"
    >
      <div className="grain" />
      <div className="blob" style={{ width: 520, height: 520, background: "#A83F32", top: -180, left: -160, opacity: 0.14 }} />

      <div className="container-x relative">
        <div className="tricolor-band tricolor-band-thick max-w-[120px] mb-8" />
        <div className="overline text-[#E2762A] mb-4" data-testid="crisis-overline">
          {pick(T.crisis.overline, lang)}
        </div>
        <h2
          className="font-serif-display serif-textured text-5xl sm:text-7xl lg:text-8xl font-light leading-[0.95] mb-14 max-w-5xl"
          data-testid="crisis-h2"
        >
          {pick(T.crisis.h2, lang)}
        </h2>

        {/* Three stark stats */}
        <div className="grid md:grid-cols-3 gap-10 md:gap-8 mb-16 md:mb-20">
          {stats.map((s, i) => (
            <div key={i} className="reveal" data-testid={`crisis-stat-${i}`}>
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8B1A0] mb-3">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div
                className="font-serif-display font-light text-6xl sm:text-7xl leading-none text-[#E2762A] mb-4"
                data-testid={`crisis-stat-num-${i}`}
              >
                {s.n}
              </div>
              <p className="text-[#D8CDBA] leading-relaxed text-base sm:text-lg max-w-[24ch]">
                {pick(s.l, lang)}
              </p>
            </div>
          ))}
        </div>

        {/* Overcrowding line */}
        <div
          className="rounded-2xl border border-white/15 bg-white/[0.03] px-6 py-5 mb-20 flex items-start gap-4"
          data-testid="crisis-overcap"
        >
          <div className="text-[#E2762A] font-serif-display text-2xl leading-none pt-0.5">113%</div>
          <p className="text-[#D8CDBA] text-sm sm:text-base leading-relaxed">
            {pick(T.crisis.over, lang)}
          </p>
        </div>

        {/* Family ripple */}
        <div className="border-t border-white/10 pt-16 grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-7">
            <h3
              className="font-serif-display text-3xl sm:text-4xl md:text-5xl font-light leading-[1.1] mb-6"
              data-testid="crisis-family-h"
            >
              {pick(T.crisis.familyH, lang)}
            </h3>
            <p className="text-[#D8CDBA] text-base sm:text-lg leading-relaxed" data-testid="crisis-family-body">
              {pick(T.crisis.familyBody, lang)}
            </p>
          </div>

          {/* Ripple visual — 1 filled figure + 5 outlined */}
          <div className="md:col-span-5" data-testid="crisis-ripple">
            <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-8">
              <div className="flex items-end gap-3 mb-5">
                {/* Filled — the undertrial */}
                <svg width="56" height="72" viewBox="0 0 40 52" fill="none" aria-hidden="true">
                  <circle cx="20" cy="10" r="9" fill="#E2762A" />
                  <path d="M4 52c0-8.8 7.2-16 16-16s16 7.2 16 16" fill="#E2762A" />
                </svg>
                {/* 5 faded family figures */}
                {[0, 1, 2, 3, 4].map((k) => (
                  <svg
                    key={k}
                    width="32"
                    height="46"
                    viewBox="0 0 40 52"
                    fill="none"
                    aria-hidden="true"
                    style={{ opacity: 0.55 - k * 0.05 }}
                  >
                    <circle cx="20" cy="10" r="9" stroke="#D8CDBA" strokeWidth="2" />
                    <path
                      d="M4 52c0-8.8 7.2-16 16-16s16 7.2 16 16"
                      stroke="#D8CDBA"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                ))}
              </div>
              <div className="grid grid-cols-6 gap-3 text-[10px] uppercase tracking-widest">
                <div className="col-span-1 text-[#E2762A]">1</div>
                <div className="col-span-5 text-[#D8CDBA]">5+</div>
              </div>
              <div className="grid grid-cols-6 gap-3 text-xs text-[#B8B1A0] mt-1">
                <div className="col-span-1 leading-tight">{pick(T.crisis.ripple1, lang)}</div>
                <div className="col-span-5 leading-tight">{pick(T.crisis.ripple2, lang)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA + source */}
        <div className="mt-16 pt-10 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p
            className="font-serif-display text-2xl sm:text-3xl font-light leading-snug max-w-2xl"
            data-testid="crisis-cta-text"
          >
            {pick(T.crisis.cta, lang)}
          </p>
          <div className="flex gap-3">
            <a href="#lead" className="btn-primary !bg-white !text-[#1F3A2C] hover:!bg-[#EAE6DF]" data-testid="crisis-cta-lead">
              {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
            </a>
            <a
              href={`tel:${T.phone.number}`}
              className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[#1F3A2C]"
              data-testid="crisis-cta-call"
            >
              <Phone size={16} strokeWidth={2} /> {T.phone.display}
            </a>
          </div>
        </div>

        <div
          className="text-xs text-[#8B8578] mt-10"
          data-testid="crisis-source"
        >
          {pick(T.crisis.source, lang)}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Understand First (repositioning) -------------------- */
function UnderstandFirst() {
  const { lang } = useLang();
  const guides = [
    { slug: "bail-process", icon: Scale, t: { en: "The bail process, explained", hi: "ज़मानत की प्रक्रिया, समझाई गई" }, d: { en: "Regular, anticipatory, interim — under BNSS 2023. Sections, courts, documents.", hi: "नियमित, अग्रिम, अंतरिम — BNSS 2023 के तहत। धाराएँ, अदालतें, दस्तावेज़।" } },
    { slug: "what-it-costs", icon: HeartHandshake, t: { en: "What it actually costs — and what you should NEVER pay", hi: "असली लागत — और क्या कभी नहीं देना है" }, d: { en: "Real fee ranges. Plus the exact list of bribes to refuse.", hi: "असली फ़ीस रेंज। साथ ही मना करने योग्य रिश्वतों की सूची।" } },
    { slug: "timeline", icon: Sparkles, t: { en: "How long bail actually takes", hi: "ज़मानत में असल में कितना समय" }, d: { en: "Day-by-day, court-by-court. No sugarcoating.", hi: "दिन-दर-दिन, अदालत-दर-अदालत। कोई लीपापोती नहीं।" } },
    { slug: "legal-terms", icon: Check, t: { en: "The legal words your family needs", hi: "आपके परिवार को ज़रूरी कानूनी शब्द" }, d: { en: "FIR, remand, chargesheet, BNS, BNSS — translated to plain language.", hi: "FIR, रिमांड, चार्जशीट, BNS, BNSS — सरल भाषा में।" } },
  ];
  return (
    <section id="understand" className="section-y bg-[#FAF9F6]" data-testid="understand-section">
      <div className="container-x">
        <div className="max-w-3xl mb-14 reveal">
          <div className="overline mb-5">{lang === "hi" ? "पहले समझिए" : "Understand first"}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight mb-5" data-testid="understand-h2">
            {lang === "hi"
              ? "हम पहले समझाते हैं। फिर वकील का सवाल आता है।"
              : "We explain first. The lawyer question comes second."}
          </h2>
          <p className="text-lg text-[#4A4640] leading-relaxed">
            {lang === "hi"
              ? "अंधेरे में रखना शोषण की शुरुआत है। इसलिए हम पहले प्रक्रिया, समय-रेखा, और असली लागत खुलकर बताते हैं — फिर आप तय कीजिए कि वकील चाहिए या नहीं।"
              : "Keeping you in the dark is how exploitation begins. So we explain the process, the timeline, and the real costs openly first — then you decide if you need a lawyer at all."}
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {guides.map((g, i) => {
            const Icon = g.icon;
            return (
              <Link
                key={g.slug}
                to={`/guide/${g.slug}`}
                className="group bg-white border border-[#D8CDBA] rounded-3xl p-7 hover:-translate-y-1 hover:shadow-lg transition-all reveal"
                data-testid={`understand-card-${g.slug}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#2C4C3B]/10 text-[#2C4C3B] flex items-center justify-center mb-4">
                  <Icon size={18} strokeWidth={1.75} />
                </div>
                <h3 className="font-serif-display text-2xl font-medium text-[#1A1A1A] leading-tight mb-2 group-hover:text-[#2C4C3B] transition-colors">
                  {pick(g.t, lang)}
                </h3>
                <p className="text-sm text-[#4A4640] leading-relaxed mb-4">{pick(g.d, lang)}</p>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2C4C3B] group-hover:gap-2 transition-all">
                  {lang === "hi" ? "पढ़ें" : "Read the guide"} <ArrowRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- How -------------------- */
function How() {
  const { lang } = useLang();
  const steps = [T.how.s1, T.how.s2, T.how.s3];
  return (
    <section id="how" className="section-y" data-testid="how-section">
      <div className="container-x">
        <div className="max-w-2xl mb-16 reveal">
          <div className="overline mb-5">{pick(T.how.overline, lang)}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight" data-testid="how-h2">{pick(T.how.h2, lang)}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((s, i) => (
            <div key={i} className="relative reveal" data-testid={`how-step-${i + 1}`}>
              <div className="editorial-num mb-6">0{i + 1}</div>
              <h3 className="font-serif-display text-2xl font-medium text-[#2C4C3B] mb-3">{pick(s.title, lang)}</h3>
              <p className="text-[#4A4640] leading-relaxed">{pick(s.body, lang)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Cases (Bail-only focus) -------------------- */
function Cases() {
  const { lang } = useLang();
  const bail = T.cases.items.bail;
  const points = [bail.points.regular, bail.points.anticipatory, bail.points.interim];
  return (
    <section id="cases" className="section-y bg-[#FAF9F6]" data-testid="cases-section">
      <div className="container-x">
        <div className="max-w-2xl mb-14 reveal">
          <div className="overline mb-5">{pick(T.cases.overline, lang)}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight" data-testid="cases-h2">{pick(T.cases.h2, lang)}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div
            className="md:col-span-8 relative bg-[#2C4C3B] text-white rounded-3xl p-10 md:p-14 overflow-hidden reveal min-h-[360px]"
            data-testid="case-bail"
          >
            <div className="grain" />
            <div className="overline text-[#D8CDBA] mb-4 relative">{pick(bail.tag, lang)}</div>
            <h3 className="font-serif-display text-5xl sm:text-6xl font-light leading-[1.02] mb-5 relative">
              {pick(bail, lang)}
            </h3>
            <p className="text-[#D8CDBA] max-w-lg relative leading-relaxed text-lg mb-8">{pick(bail.desc, lang)}</p>
            <a href="#lead" className="inline-flex items-center gap-2 text-white border-b border-white/40 pb-1 hover:border-white transition relative" data-testid="case-bail-cta">
              {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
            </a>
            <Scale className="absolute bottom-8 right-8 text-white/15" size={140} strokeWidth={1} />
          </div>

          <div className="md:col-span-4 space-y-4 reveal">
            {points.map((p, i) => (
              <div
                key={i}
                className="bg-white border border-[#D8CDBA] rounded-3xl p-6 flex items-start gap-4"
                data-testid={`case-bail-point-${i}`}
              >
                <div className="editorial-num text-[#C46B2F]" style={{ fontSize: "2.5rem" }}>0{i + 1}</div>
                <p className="text-[#4A4640] leading-relaxed text-base pt-2">{pick(p, lang)}</p>
              </div>
            ))}
            <div className="text-xs uppercase tracking-[0.2em] text-[#C46B2F] pt-4" data-testid="cases-more">
              {pick(T.cases.more, lang)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Social Proof -------------------- */
function Social() {
  const { lang } = useLang();
  const items = [T.social.t1, T.social.t2, T.social.t3];
  return (
    <section id="stories" className="section-y" data-testid="social-section">
      <div className="container-x">
        <div className="max-w-2xl mb-14 reveal">
          <div className="overline mb-5">{pick(T.social.overline, lang)}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight" data-testid="social-h2">
            {pick(T.social.h2, lang)}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {items.map((t, i) => (
            <figure
              key={i}
              className="bg-white rounded-3xl p-8 border border-[#D8CDBA]/70 reveal"
              data-testid={`testimonial-${i}`}
            >
              <Sparkles className="text-[#C46B2F] mb-5" size={22} strokeWidth={1.5} />
              <blockquote className="font-serif-display text-xl leading-snug text-[#1A1A1A] mb-6">
                “{pick(t.quote, lang)}”
              </blockquote>
              <figcaption className="text-sm text-[#736F66]">
                <span className="text-[#2C4C3B] font-medium">{t.name}</span> · {pick(t.city, lang)}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- About -------------------- */
function About() {
  const { lang } = useLang();
  return (
    <section className="section-y bg-[#EAE6DF]" data-testid="about-section">
      <div className="container-x grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5 reveal">
          <div className="rounded-[2rem] overflow-hidden aspect-[4/5]">
            <img
              src="https://images.pexels.com/photos/11739907/pexels-photo-11739907.jpeg"
              alt="Warm golden light patterns"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="md:col-span-7 reveal">
          <div className="overline mb-5">{pick(T.about.overline, lang)}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-[1.05] mb-6" data-testid="about-h2">
            {pick(T.about.h2, lang)}
          </h2>
          <p className="text-lg text-[#4A4640] leading-relaxed whitespace-pre-line" data-testid="about-body">
            {pick(T.about.body, lang)}
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Fees -------------------- */
function Fees() {
  const { lang } = useLang();
  const f = T.fees.a;
  const points = [f.points.one, f.points.two, f.points.three];
  return (
    <section id="fees" className="section-y" data-testid="fees-section">
      <div className="container-x">
        <div className="max-w-2xl mb-14 reveal">
          <div className="overline mb-5">{pick(T.fees.overline, lang)}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight" data-testid="fees-h2">
            {pick(T.fees.h2, lang)}
          </h2>
        </div>
        <div className="max-w-4xl mx-auto rounded-3xl p-10 md:p-14 border border-[#D8CDBA] bg-white reveal" data-testid="fee-card">
          <div className="flex items-start gap-6 mb-8">
            <div className="w-14 h-14 rounded-full bg-[#2C4C3B]/10 text-[#2C4C3B] flex items-center justify-center flex-shrink-0">
              <HeartHandshake size={26} strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-serif-display text-4xl md:text-5xl font-light text-[#2C4C3B] mb-4 leading-tight">
                {pick(f.title, lang)}
              </h3>
              <p className="text-lg text-[#4A4640] leading-relaxed">{pick(f.body, lang)}</p>
            </div>
          </div>
          <ul className="grid sm:grid-cols-3 gap-4 pt-6 border-t border-[#D8CDBA]">
            {points.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#4A4640]" data-testid={`fee-point-${i}`}>
                <Check size={18} className="text-[#2C4C3B] mt-0.5 flex-shrink-0" strokeWidth={2} />
                <span>{pick(p, lang)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* -------------------- FAQ -------------------- */
function FAQ() {
  const { lang } = useLang();
  return (
    <section id="faq" className="section-y bg-[#FAF9F6]" data-testid="faq-section">
      <div className="container-x grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4 reveal">
          <div className="overline mb-5">{pick(T.faq.overline, lang)}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight" data-testid="faq-h2">
            {pick(T.faq.h2, lang)}
          </h2>
        </div>
        <div className="md:col-span-8 reveal">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {T.faq.items.map((it, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#D8CDBA]">
                <AccordionTrigger className="text-left font-serif-display text-xl md:text-2xl font-medium text-[#1A1A1A] hover:no-underline py-6" data-testid={`faq-q-${i}`}>
                  {pick(it.q, lang)}
                </AccordionTrigger>
                <AccordionContent className="text-[#4A4640] text-base leading-relaxed pb-6" data-testid={`faq-a-${i}`}>
                  {pick(it.a, lang)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Lead Form -------------------- */
function LeadForm() {
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", city: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(lang === "hi" ? "कृपया नाम और फोन नंबर भरें।" : "Please enter your name and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await fetch(`${BACKEND}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, language: lang }),
      });
      if (!resp.ok) throw new Error("bad");
      trackEvent("generate_lead", {
        language: lang,
        has_city: Boolean(form.city),
      });
      trackServerEvent("generate_lead", { placement: "lead-form" });
      toast.success(pick(T.form.success, lang));
      setDone(true);
    } catch (err) {
      trackEvent("lead_submit_failed", { language: lang });
      toast.error(pick(T.form.error, lang));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="lead" className="section-y bg-[#2C4C3B] text-white relative overflow-hidden" data-testid="lead-section">
      <div className="blob" style={{ width: 460, height: 460, background: "#E2762A", top: -180, right: -140, opacity: 0.20 }} />
      <div className="container-x grid md:grid-cols-12 gap-14 relative">
        <div className="md:col-span-5 reveal">
          <div className="overline text-[#D8CDBA] mb-5">{pick(T.form.overline, lang)}</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight mb-6" data-testid="lead-h2">
            {pick(T.form.h2, lang)}
          </h2>
          <p className="text-[#D8CDBA] leading-relaxed max-w-md">{pick(T.form.sub, lang)}</p>
          <div className="mt-10 space-y-3 text-sm text-[#D8CDBA]">
            {[T.hero.trust1, T.hero.trust2, T.hero.trust3].map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <Check size={16} strokeWidth={2} /> {pick(t, lang)}
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-7 reveal">
          {done ? (
            <div className="bg-white text-[#1A1A1A] rounded-3xl p-10" data-testid="lead-success">
              <Sparkles className="text-[#C46B2F] mb-4" size={28} strokeWidth={1.5} />
              <h3 className="font-serif-display text-3xl font-medium mb-3">
                {lang === "en" ? "Thank you. You're not alone anymore." : "धन्यवाद। अब आप अकेले नहीं हैं।"}
              </h3>
              <p className="text-[#4A4640]">{pick(T.form.success, lang)}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white text-[#1A1A1A] rounded-3xl p-8 md:p-10 space-y-5" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#736F66] mb-2 block">{pick(T.form.name, lang)}</label>
                  <input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#D8CDBA] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] bg-[#FAF9F6]"
                    required
                    data-testid="lead-input-name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#736F66] mb-2 block">{pick(T.form.phone, lang)}</label>
                  <input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#D8CDBA] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] bg-[#FAF9F6]"
                    required
                    data-testid="lead-input-phone"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-[#736F66] mb-2 block">{pick(T.form.city, lang)}</label>
                <input
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#D8CDBA] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] bg-[#FAF9F6]"
                  data-testid="lead-input-city"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full sm:w-auto justify-center disabled:opacity-50"
                data-testid="lead-submit"
              >
                {submitting ? "…" : pick(T.form.submit, lang)} <ArrowRight size={16} />
              </button>
              <p className="text-xs text-[#736F66]" data-testid="lead-form-note">
                {lang === "hi"
                  ? "बस 3 चीज़ें — बाक़ी बात फोन पर होगी।"
                  : "Just 3 fields — everything else happens on the call."}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Closing -------------------- */
function Closing() {
  const { lang } = useLang();
  return (
    <section className="section-y text-center" data-testid="closing-section">
      <div className="container-x max-w-3xl mx-auto reveal">
        <h2 className="font-serif-display serif-textured text-4xl sm:text-6xl font-light leading-[1.05] mb-6" data-testid="closing-h2">
          {pick(T.closing.h2, lang)}
        </h2>
        <p className="text-lg text-[#4A4640] mb-10 max-w-xl mx-auto">{pick(T.closing.body, lang)}</p>
        <a href="#lead" className="btn-primary" data-testid="closing-cta">
          {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
        </a>
      </div>
    </section>
  );
}

/* -------------------- Landing -------------------- */
export default function Landing() {
  const { lang } = useLang();
  return (
    <>
      <SEO
        title={
          lang === "hi"
            ? "एक भरोसेमंद वकील, आपके अपनों की ज़मानत के लिए"
            : "A Trusted Lawyer for Your Loved One's Bail"
        }
        description={
          lang === "hi"
            ? "Rihai भारत के परिवारों को सत्यापित ज़मानत वकीलों से सीधे जोड़ता है — असली हाई कोर्ट नतीजों पर आधारित। कोई मुंशी नहीं, कोई दलाल नहीं।"
            : "Rihai connects Indian families with verified bail lawyers — ranked by real High Court outcomes. No munshis. No dallaals. Empathetic legal support when it matters most."
        }
        path="/"
      />
      <Nav />
      <main>
        <Hero />
        <Problem />
        <Crisis />
        <UnderstandFirst />
        <How />
        <Cases />
        <Social />
        <About />
        <Fees />
        <FAQ />
        <LeadForm />
        <Closing />
      </main>
      <Footer />
    </>
  );
}
